const QUOTE_MARKER = /ORÇAMENTO DE PEÇAS\s*\(catálogo\)/i;

const stripMarkdown = (line) => String(line || "").replace(/\*\*/g, "").trim();

const isPlaceholder = (value) => {
  const t = String(value ?? "").trim();
  return !t || t === "-" || t === "—";
};

const parseItemLine = (line) => {
  const withDesc = line.match(/^(\d+)\.\s*(\S+)\s*[–-]\s*(.+?)\s+x(\d+)\s*$/i);
  if (withDesc) {
    return {
      sku: withDesc[2].trim(),
      description: withDesc[3].trim(),
      qty: Math.max(1, parseInt(withDesc[4], 10) || 1),
    };
  }

  const simple = line.match(/^(\d+)\.\s*(.+?)\s+x(\d+)\s*$/i);
  if (!simple) return null;

  const body = simple[2].trim();
  const dashMatch = body.match(/^(\S+)\s*[–-]\s*(.+)$/);
  if (dashMatch) {
    return {
      sku: dashMatch[1].trim(),
      description: dashMatch[2].trim(),
      qty: Math.max(1, parseInt(simple[3], 10) || 1),
    };
  }

  return {
    sku: body,
    description: null,
    qty: Math.max(1, parseInt(simple[3], 10) || 1),
  };
};

const parseVehicleLine = (line) => {
  const vehicle = { vinOrPlates: null, seriesModel: null, year: null };
  const vinMatch = line.match(/VIN\/Placa\(s\)\s*:\s*([^•]+)/i);
  const seriesMatch = line.match(/Série\/Modelo\s*:\s*([^•]+)/i);
  const yearMatch = line.match(/Ano\s*:\s*(.+)$/i);
  if (vinMatch) vehicle.vinOrPlates = vinMatch[1].trim();
  if (seriesMatch) vehicle.seriesModel = seriesMatch[1].trim();
  if (yearMatch) vehicle.year = yearMatch[1].trim();
  Object.keys(vehicle).forEach((key) => {
    if (isPlaceholder(vehicle[key])) vehicle[key] = null;
  });
  return vehicle;
};

/**
 * Detecta e estrutura solicitações de orçamento de peças enviadas pelo catálogo (balcão Scania).
 * Retorna null se o texto não corresponder ao formato esperado.
 */
export function parsePartsQuoteRequest(rawText) {
  if (rawText == null) return null;
  const text = String(rawText).trim();
  if (!text || !QUOTE_MARKER.test(text)) return null;

  const result = {
    isQuoteRequest: true,
    source: "catalog",
    company: null,
    contact: null,
    email: null,
    items: [],
    vehicle: { vinOrPlates: null, seriesModel: null, year: null },
    notes: null,
    context: null,
    page: null,
  };

  let inItems = false;

  for (const rawLine of text.split("\n")) {
    const line = stripMarkdown(rawLine);
    if (!line) continue;

    if (/^empresa\s*:/i.test(line)) {
      inItems = false;
      const value = line.replace(/^empresa\s*:\s*/i, "").trim();
      result.company = isPlaceholder(value) ? null : value;
      continue;
    }
    if (/^responsável\s*:/i.test(line)) {
      inItems = false;
      const value = line.replace(/^responsável\s*:\s*/i, "").trim();
      result.contact = isPlaceholder(value) ? null : value;
      continue;
    }
    if (/^contato\s*:/i.test(line)) {
      inItems = false;
      const value = line.replace(/^contato\s*:\s*/i, "").trim();
      result.email = isPlaceholder(value) ? null : value;
      continue;
    }
    if (/^itens\s*:/i.test(line)) {
      inItems = true;
      continue;
    }
    if (/^veículo\s*:/i.test(line)) {
      inItems = false;
      const vehiclePart = line.replace(/^veículo\s*:\s*/i, "");
      result.vehicle = parseVehicleLine(vehiclePart);
      continue;
    }
    if (/^observações\s*:/i.test(line)) {
      inItems = false;
      const value = line.replace(/^observações\s*:\s*/i, "").trim();
      result.notes = isPlaceholder(value) ? null : value;
      continue;
    }
    if (/^contexto\s*:/i.test(line)) {
      inItems = false;
      const value = line.replace(/^contexto\s*:\s*/i, "").trim();
      result.context = isPlaceholder(value) ? null : value;
      continue;
    }
    if (/^página\s*:/i.test(line)) {
      inItems = false;
      const value = line.replace(/^página\s*:\s*/i, "").trim();
      result.page = normalizeQuotePageUrl(value);
      continue;
    }

    if (inItems) {
      const item = parseItemLine(line);
      if (item) result.items.push(item);
    }
  }

  return result;
}

export function isPartsQuoteRequestMessage(message) {
  return Boolean(parsePartsQuoteRequest(message?.text));
}

export function hasVehicleInfo(vehicle) {
  if (!vehicle) return false;
  return Boolean(vehicle.vinOrPlates || vehicle.seriesModel || vehicle.year);
}

export function extractSearchTerm(context) {
  if (!context) return null;
  const match = String(context).match(/Termo pesquisado:\s*"([^"]+)"/i);
  return match?.[1]?.trim() || null;
}

const CONTEXT_FIELD_DEFS = [
  { key: "searchTerm", label: "Termo pesquisado", pattern: /Termo pesquisado:\s*"([^"]+)"/i, pick: (m) => m?.[1]?.trim() || null },
  { key: "category", label: "Categoria", pattern: /Categoria:\s*([^|]+)/i, pick: (m) => m?.[1]?.trim() || null },
  { key: "subcategory", label: "Subcategoria", pattern: /Subcategoria:\s*([^|]+)/i, pick: (m) => m?.[1]?.trim() || null },
  { key: "application", label: "Aplicação", pattern: /Aplicação:\s*([^|]+)/i, pick: (m) => m?.[1]?.trim() || null },
  { key: "vehicleType", label: "Tipo de veículo", pattern: /Tipo de veículo:\s*([^|]+)/i, pick: (m) => m?.[1]?.trim() || null },
  { key: "model", label: "Modelo", pattern: /Modelo:\s*([^|]+)/i, pick: (m) => m?.[1]?.trim() || null },
  { key: "tags", label: "Tags", pattern: /Tags:\s*([^|]+)/i, pick: (m) => m?.[1]?.trim() || null },
];

/** Estrutura o contexto do catálogo (filtros da navegação do cliente). */
export function parseQuoteContext(context) {
  const raw = context ? String(context).trim() : "";
  const result = {
    searchTerm: null,
    category: null,
    subcategory: null,
    application: null,
    vehicleType: null,
    model: null,
    tags: null,
    entries: [],
  };

  if (!raw) return result;

  for (const def of CONTEXT_FIELD_DEFS) {
    const match = raw.match(def.pattern);
    const value = def.pick(match);
    if (value && !isPlaceholder(value)) {
      result[def.key] = value;
      result.entries.push({ key: def.key, label: def.label, value });
    }
  }

  return result;
}

export function hasQuoteContext(contextParsed) {
  return Boolean(contextParsed?.entries?.length);
}

const normalizeLabel = (value) =>
  String(value ?? "")
    .normalize("NFKC")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const namesSimilar = (left, right) => {
  const a = normalizeLabel(left);
  const b = normalizeLabel(right);
  if (!a || !b) return false;
  if (a === b || a.includes(b) || b.includes(a)) return true;

  const wordsA = a.split(" ").filter(Boolean);
  const wordsB = b.split(" ").filter(Boolean);
  if (wordsA.length >= 2 && wordsB.length >= 2) {
    return wordsA[0] === wordsB[0] && wordsA[wordsA.length - 1] === wordsB[wordsB.length - 1];
  }
  return false;
};

/** Separa código numérico inicial do nome da empresa (ex.: "61137611 Fulano"). */
export function parseCompanyField(company) {
  if (!company) return { code: null, name: null, full: null };
  const match = String(company).trim().match(/^(\d+)\s+(.+)$/);
  if (match) {
    return { code: match[1], name: match[2].trim(), full: company.trim() };
  }
  return { code: null, name: String(company).trim(), full: String(company).trim() };
}

/** Monta rótulos do card evitando repetir nome/empresa/responsável. */
export function buildQuotePresentation({ quote, senderName }) {
  const company = parseCompanyField(quote?.company);
  const contact = quote?.contact?.trim() || null;
  const sender = senderName?.trim() || null;

  const contactMatchesSender = namesSimilar(contact, sender);
  const companyNameMatchesSender = namesSimilar(company.name, sender);
  const companyNameMatchesContact = namesSimilar(company.name, contact);

  const primaryName = contactMatchesSender
    ? sender
    : contact || sender || company.name || null;

  let companyLine = null;
  if (company.code && (companyNameMatchesSender || companyNameMatchesContact)) {
    companyLine = company.code;
  } else if (company.full && !companyNameMatchesSender) {
    companyLine = company.full;
  } else if (company.code) {
    companyLine = company.code;
  }

  const items = Array.isArray(quote?.items) ? quote.items : [];
  const totalQty = items.reduce((sum, item) => sum + (item.qty || 0), 0);

  return {
    primaryName,
    companyLine,
    email: quote?.email || null,
    itemCount: items.length,
    totalQty: totalQty || items.length,
    showContactSeparately: Boolean(contact && !contactMatchesSender && !companyNameMatchesContact),
    contact,
  };
}

export function formatCatalogPageUrl(url) {
  const full = normalizeQuotePageUrl(url);
  if (!full) return null;

  try {
    const parsed = new URL(full);
    const host = parsed.hostname.replace(/^www\./i, "");
    const path = parsed.pathname && parsed.pathname !== "/" ? parsed.pathname : "";
    const query = parsed.search || "";
    const label = `${host}${path}${query}`;

    return {
      full,
      host,
      label,
      short: label,
    };
  } catch {
    return { full, host: full, label: full, short: full };
  }
}

/** Normaliza URL de origem do catálogo para uso em links (href). */
export function normalizeQuotePageUrl(raw) {
  if (raw == null) return null;

  let value = String(raw).trim();
  if (!value || isPlaceholder(value)) return null;

  const hrefMatch = value.match(/href=["']([^"']+)["']/i);
  if (hrefMatch?.[1]) value = hrefMatch[1].trim();

  value = value.replace(/<[^>]+>/g, " ").trim();
  value = value
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");

  value = value.replace(/…$/u, "").replace(/\.\.\.$/, "").trim();

  while (/[.,!?;:]$/.test(value)) {
    value = value.slice(0, -1);
  }

  const urlMatch = value.match(/(?:https?:\/\/|www\.)[^\s<>"{}|\\^`[\]]+/i);
  if (urlMatch?.[0]) value = urlMatch[0];

  if (/^www\./i.test(value)) {
    value = `https://${value}`;
  } else if (!/^https?:\/\//i.test(value) && /^[\w.-]+\.[a-z]{2,}/i.test(value)) {
    value = `https://${value}`;
  }

  try {
    const parsed = new URL(value);
    if (!["http:", "https:"].includes(parsed.protocol)) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

export function quoteHasExtraDetails(quote, contextParsed) {
  if (!quote) return false;
  const vehicleVisible = hasVehicleInfo(quote.vehicle);
  const contextCount = contextParsed?.entries?.length || 0;
  return Boolean(quote.notes || vehicleVisible || contextCount > 3);
}
